import { Api } from '@axiom/ui';
import { TagEntity } from '@axiom/domain';

import { EnvUtil } from '../utils/env-util';
import type { Tag } from '../types/generated/tag';

import { TagApi } from './generated/tag-api';

/**
 * Tag reads/writes are split across two backends. The catalog read
 * (`GET /tags`) is in the dark-roast OpenAPI spec and is served by the
 * generated `TagApi` on the Java/envoy domain (`EnvUtil.apiEnvoyUrl`) —
 * `readTags` here just delegates to it. The mutations are NOT in the spec;
 * they live on the legacy Node `api` (client domain, `EnvUtil.clientApiBase`),
 * so they live in this class. Their responses normalize through the generated
 * `@axiom/domain` `TagEntity` (shared `Tag` cache key) so these writes and the
 * generated read share the same normalized cache record.
 */
type TagWriteBody = Pick<Tag, 'name'> & Partial<Pick<Tag, 'type' | 'parentId'>>;

class LegacyTagsApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.clientApiBase,
    });
  }

  readTags() {
    return TagApi.readTags();
  }

  async createTags({ body }: { body: TagWriteBody[] }) {
    return super.write<{ data: Tag[] }>({
      endpoint: '/tags',
      method: 'POST',
      body,
      schema: { data: [TagEntity] },
    });
  }

  async updateTag({ id, body }: { id: Tag['id']; body: TagWriteBody }) {
    return super.write<{ data: Tag }>({
      endpoint: `/tags/${id}`,
      method: 'PATCH',
      body,
      schema: { data: TagEntity },
    });
  }

  async deleteTag({ id }: { id: Tag['id'] }) {
    return super.write<{ data: { status: string } }>({
      endpoint: `/tags/${id}`,
      method: 'DELETE',
      // Passing a schema opts this DELETE into the Data Client path so the Api
      // drops the deleted tag from the cached `/tags` list off the mutation —
      // no manual refresh. The status-envelope response is not normalized.
      schema: { data: TagEntity },
    });
  }
}

export const LegacyTagsApi = new LegacyTagsApiClass();
